<template>
  <div class="page-content--bgf7">
    <section class="au-breadcrumb2">
      <div class="container m-b-60">
        <div class="row">
          <div class="col-md-12">
            <v-list-item>
              <v-list-item-avatar>
                <v-img
                  contain
                  class="grey"
                  :src="channel.watermarkPicture && channel.watermark.enabled ?
                    channel.watermarkPicture : channel.logo"
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  <span v-if="channel.autoScheduler">Auto-Scheduler</span>
                  <span v-else-if="!loading">Manual Scheduler</span>
                </v-list-item-title>
                <v-list-item-subtitle v-if="channel.autoSchedulerDepth">
                  Forward scheduling depth: {{ channel.autoSchedulerDepthStr }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  text
                  color="primary"
                  @click="goToRules()"
                >
                  <i class="fas fa-cog m-r-5" />Manage Scheduler
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 p-t-0 p-l-15">
            <v-card dark>
              <iframe
                id="streamFrameSrc"
                :src="streamFrameSrc"
                width="100%"
                scrolling="no"
                height="198"
                frameborder="0"
              />
            </v-card>
          </div>
          <div class="col-md-8 p-l-10 p-t-0 p-b-0">
            <v-card>
              <v-row>
                <div class="col-md-5 p-t-0 p-b-0 p-r-0">
                  <div
                    v-if="loading"
                    class="el-loading-mask"
                  >
                    <div class="el-loading-spinner">
                      <svg
                        viewBox="25 25 50 50"
                        class="circular"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="20"
                          fill="none"
                          class="path"
                        />
                      </svg>
                    </div>
                  </div>
                  <table class="table table-sm table-bordered">
                    <tr
                      v-for="w in weeks"
                      :key="w.text"
                      class="pointer"
                      :class="{ 'primary white--text': w.text === week.text }"
                      @click="changeWeek(w)"
                    >
                      <td
                        v-ripple
                        class="text-body-2"
                        style="width: 260px;"
                      >
                        <i
                          v-if="w.locked"
                          class="fas fa-lock p-r-10 grey--text"
                        />
                        <v-badge
                          v-if="w.text === weekObj.text"
                          :color="w.text === week.text ? 'white': 'primary'"
                          offset-x="0"
                          offset-y="8"
                          dot
                        >
                          {{ w.text }}
                        </v-badge>
                        <span v-else>
                          {{ w.text }}
                        </span>
                      </td>
                      <td
                        v-ripple
                        class="text-body-2"
                      >
                        {{ w.occupancy }}%
                      </td>
                    </tr>
                  </table>
                </div>
                <div class="col-md-7 p-t-0 p-b-0 p-l-0">
                  <v-card>
                    <div
                      v-if="loading"
                      class="el-loading-mask"
                    >
                      <div class="el-loading-spinner">
                        <svg
                          viewBox="25 25 50 50"
                          class="circular"
                        >
                          <circle
                            cx="50"
                            cy="50"
                            r="20"
                            fill="none"
                            class="path"
                          />
                        </svg>
                      </div>
                    </div>
                    <table class="table table-sm table-bordered">
                      <tr
                        v-for="day in week.days"
                        :key="day.v"
                        class="pointer"
                        :class="{ 'primary white--text': calendarDay === day.v }"
                        @click="changeDay(day)"
                      >
                        <td
                          v-ripple
                          class="text-body-2"
                          style="width: 150px;"
                        >
                          <i class="zmdi zmdi-calendar p-r-10" />
                          <v-badge
                            v-if="day.v === currentDay"
                            :color="calendarDay === day.v ? 'white': 'primary'"
                            offset-x="0"
                            offset-y="8"
                            dot
                          >
                            {{ day.text }}
                          </v-badge>
                          <span v-else>
                            {{ day.text }}
                          </span>
                        </td>
                        <td class="text-body-2">
                          <span
                            :class="{
                              'green--text text--darken-3': day.occupancy >= 90 && calendarDay !== day.v,
                              'white--text': calendarDay === day.v
                            }"
                          >
                            {{ day.occupancy }}%
                          </span>
                          <span
                            v-if="day.events"
                            class="p-l-20"
                          >
                            {{ day.events }}
                            <span v-if="!day.overlays.length">
                              event<span v-if="day.events === 0 || day.events > 1">s</span>
                            </span>
                          </span>
                          <v-icon
                            v-if="day.overlays.length"
                            small
                            class="p-l-20"
                          >
                            mdi-image-filter-none
                          </v-icon>
                          <span
                            v-if="day.overlays.length"
                            style="color: #FF8A80"
                          >
                            <strong>{{ day.overlays.length }}</strong>
                          </span>
                        </td>
                      </tr>
                    </table>
                  </v-card>
                </div>
              </v-row>
            </v-card>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 p-l-15 p-b-0 text-left text-overline">
            On now:
            <span class="p-r-5 text--pointer">
              {{ currentEvent ? currentEvent.program.name : '-----' | truncate(30) }}
            </span>
          </div>
          <div
            v-if="currentDayObj"
            class="col-md-8 title-5 text-left"
          >
            <span class="primary--text">
              <i class="zmdi zmdi-calendar p-r-5" />
              {{ moment(currentDayObj.start).utc().format('dddd, D MMMM') }}
            </span>
            <v-btn
              v-if="currentDayObj.events && !week.locked"
              class="m-l-30"
              text
              @click="clearDayDialog = true"
            >
              <i class="mdi mdi-delete-sweep p-r-5" />clear day
            </v-btn>
            <v-btn
              v-if="currentDayObj.events === 0 && !week.locked"
              class="m-l-10"
              text
              @click="randomDayDialog = true"
            >
              <i class="fas fa-random p-r-5" />random day's EPG
            </v-btn>
            <v-btn
              v-if="week.locked && !channel.autoScheduler"
              class="m-l-10"
              text
              @click.prevent="lockWeek(false)"
            >
              <i class="fas fa-lock-open p-r-5" />
              Unlock week
            </v-btn>
            <v-btn
              v-if="!week.locked"
              class="m-l-10"
              text
              color="primary"
              @click.prevent="lockWeek(true)"
            >
              <i class="fas fa-lock p-r-5" />
              Lock week
            </v-btn>
          </div>
          <div
            class="col-md-7 pr-0 p-t-0"
            style="padding-left: 15px !important;"
          >
            <v-sheet
              v-if="channel.autoScheduler"
              max-height="780"
            >
              <v-card :loading="dayLoading">
                <table
                  v-if="events.length > 1"
                  class="table table-bordered table-sm table-scheduler"
                >
                  <tbody>
                    <tr
                      v-for="(event, i) in events"
                      :key="'event'+i"
                      :class="{
                        'current-event': currentEvent && event.id === currentEvent.id,
                        'orange lighten-5': event.program.subType === 'ad'
                          && !(selectedEvent.event && event.id === selectedEvent.event.id),
                        'primary white--text': selectedEvent.event && event.id === selectedEvent.event.id,
                      }"
                      @click="changeEventFromAutoScheduler(event)"
                    >
                      <td
                        style="width: 25px;"
                        class="text-body-2"
                      >
                        #{{ events.length - i }}
                      </td>
                      <td
                        style="width: 95px !important;"
                        :class="{ 'red--text': event.overlap }"
                        class="p-l-10 text-body-2"
                      >
                        <i class="zmdi zmdi-calendar p-r-5" />
                        {{ moment(event.start).format('HH:mm') }}
                      </td>
                      <td
                        style="width: 95px !important;"
                        :class="{ 'red--text': event.overlap }"
                        class="p-l-10 text-body-2"
                      >
                        <i class="zmdi zmdi-calendar-close p-r-5" />
                        {{ moment(event.end).format('HH:mm') }}
                      </td>
                      <td v-if="i === 0 && currentDayObj.v === currentDay && !event.id">
                        <v-progress-linear
                          color="teal"
                          class="m-t-10"
                          buffer-value="0"
                          stream
                        />
                      </td>
                      <td
                        v-else
                        class="p-l-10 text-body-2"
                      >
                        <template>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                v-on="on"
                              >
                                <span
                                  v-if="event.program.containsAd"
                                  class="block-ad m-r-5"
                                >
                                  <i class="mdi mdi-play" />ad
                                </span>
                                {{ event.program.name | truncate(75) }}
                              </span>
                            </template>
                            {{ event.program.name }}
                          </v-tooltip>
                          <v-progress-linear
                            v-if="currentEvent && event.id === currentEvent.id"
                            :value="playedProgress"
                            color="blue lighten-2"
                            class="ma-0 pa-0"
                            height="5"
                          />
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div
                  v-else-if="!loading"
                  class="text-center p-t-50 p-b-50"
                >
                  No data
                </div>
              </v-card>
            </v-sheet>
            <v-sheet
              v-if="!channel.autoScheduler && !loading"
              max-height="780"
            >
              <v-card>
                <div class="text-center p-t-50 p-b-50">
                  No data
                </div>
              </v-card>
            </v-sheet>
          </div>
          <!-- Event -->
          <div class="col-md-5 p-t-0">
            <v-card
              v-if="!newEventDialog && selectedEvent.event.program && selectedEvent.event.program.id"
              class="p-b-5"
              :loading="selectedEvent && selectedEvent.event.loading === true"
            >
              <v-card-title class="p-b-5 p-t-5 title-6">
                Program ID: {{ selectedEvent.event.program.id }}
                <v-spacer />
                <v-btn
                  text
                  icon
                  :disabled="week.locked"
                  color="red lighten-2"
                  class="ma-0"
                  @click="deleteEvent"
                >
                  <i class="fa fa-trash-alt" />
                </v-btn>
              </v-card-title>
              <v-divider class="ma-0" />
              <table class="event-info">
                <tr>
                  <td
                    class="grey--text p-l-15"
                    style="width: 120px;"
                  >
                    Title:
                  </td>
                  <td
                    class="primary--text"
                    style="word-break: break-all;"
                  >
                    {{ selectedEvent.event.program.name }}
                  </td>
                  <td
                    style="vertical-align: top; width: 85px;"
                    rowspan="4"
                  >
                    <v-list-item-avatar
                      tile
                      class="p-l-5"
                      height="120"
                      width="80"
                    >
                      <v-img
                        :src="selectedEvent.event.program.posterId ?
                          '/api/v1/upload/' + selectedEvent.event.program.posterId + '?img.crop=120x80' : defaultPoster"
                      />
                    </v-list-item-avatar>
                  </td>
                </tr>
                <tr v-if="selectedEvent.event.program.assetId">
                  <td class="grey--text p-l-15">
                    Asset ID:
                  </td>
                  <td>
                    {{ selectedEvent.event.program.assetId }}
                  </td>
                </tr>
                <tr>
                  <td class="grey--text p-l-15">
                    Start time:
                  </td>
                  <td>
                    {{ moment(selectedEvent.event.start).format('MMM D, YYYY HH:mm:ss') }}
                  </td>
                </tr>
                <tr>
                  <td class="grey--text p-l-15">
                    End time:
                  </td>
                  <td>
                    {{ moment(selectedEvent.event.end).format('MMM D, YYYY HH:mm:ss') }}
                  </td>
                </tr>
                <tr>
                  <td class="grey--text p-l-15">
                    Duration:
                  </td>
                  <td>
                    {{ moment.utc(selectedEvent.event.program.duration).format('HH\\h mm\\m ss\\s') }}
                  </td>
                </tr>
                <tr>
                  <td
                    class="grey--text p-l-15"
                    style="vertical-align: top;"
                  >
                    Videos:
                  </td>
                  <td>
                    <template v-for="(item, i) in selectedEvent.event.program.items">
                      <a
                        v-if="item.streamURL"
                        :key="item.id"
                        href="#"
                        @click.prevent="playStreamByURL(item.streamURL,
                                                        item.name, '' )"
                      >
                        <v-icon
                          size="16"
                          color="primary"
                        >mdi-play</v-icon>
                        {{ item.type }}
                        ({{ moment.utc(item.duration).format('HH\\h mm\\m ss\\s') }} sec)
                      </a>
                      <span
                        v-else
                        :key="item.id"
                      >
                        <br v-if="i > 0">
                        {{ item.type }}
                        ({{ moment.utc(item.duration).format('HH\\h mm\\m ss\\s') }} sec)
                      </span>
                    </template>
                  </td>
                </tr>
                <tr v-if="selectedEvent.event.program.tags.length">
                  <td class="grey--text p-l-15 tags">
                    Tags:
                  </td>
                  <td colspan="2">
                    <span
                      v-for="(tag, i) in selectedEvent.event.program.tags"
                      :key="'tag' + tag.id"
                      :class="{ 'm-l-10': i > 0 }"
                      class="block-email"
                    >
                      <i class="mdi mdi-label p-r-5" />
                      {{ tag.name }}
                    </span>
                  </td>
                </tr>
                <tr v-if="selectedEvent.event.program.pubTime">
                  <td class="grey--text p-l-15">
                    Pub date:
                  </td>
                  <td colspan="2">
                    {{ moment(selectedEvent.event.program.pubTime).format('MMM D, YYYY HH:mm:ss') }}
                  </td>
                </tr>
                <tr>
                  <td
                    class="grey--text p-l-15"
                    style="vertical-align: top;"
                  >
                    Description:
                  </td>
                  <td
                    colspan="2"
                    class="p-r-15"
                  >
                    {{ selectedEvent.event.program.description || 'N/A' }}
                  </td>
                </tr>
              </table>
            </v-card>
            <v-card
              v-else
              class="text-center p-t-50 p-b-50"
            >
              No data
            </v-card>
          </div>
        </div>
      </div>
    </section>
    <!-- Player dialog -->
    <v-bottom-sheet
      v-model="openPlayer"
      max-width="960"
      :persistent="false"
    >
      <v-card tile>
        <iframe
          id="serviceFrameSend"
          :src="frameSrc"
          width="100%"
          height="380"
          frameborder="0"
        />
        <v-list>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ player.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ player.description }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer />
          <v-btn
            depressed
            @click="closeStream()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
  import moment from 'moment-timezone'
  import {
    processChannel, processTimezone, processProgram2, processDistribution,
  } from '@/store/converters'

  export default {
    data () {
      return {
        polling: null,
        currentT: null,
        currentUnixTime: moment().utc().unix() * 1000,
        today: null,
        tzOffset: 0,
        currentDayObj: null,
        currentDay: null,
        calendarDay: '',
        dayLoading: false,
        week: {
          text: '',
          days: [],
        },
        weeks: [],
        weekObj: null,
        activeCol: -1,
        //
        calendarInterval: {
          current: 0,
          min: 48,
          max: 1200,
        },
        //
        channel: {
          streamURL: '',
        },
        streamFrameSrc: '',
        //
        allEvents: [],
        events: [],
        //
        currentEvent: null,
        selectedEvent: {
          loading: false,
          event: {},
        },
        //
        newEventDialog: false,
        newProgram: {
          loaded: false,
          type: 'once',
          date: '',
        },
        //
        openPlayer: false,
        frameSrc: '',
        player: {
          name: '',
          description: '',
        },
        //
        clearDayDialog: false,
        randomDayDialog: false,
        randomDay: {
          minDuration: 2,
          maxDuration: 300,
        },
      }
    },
    computed: {
      loading () {
        return this.$store.getters.isDataLoaded
      },
      defaultIcon () {
        return '/images/ch_icon.png'
      },
      defaultPoster () {
        return '/images/poster2x3.jpg'
      },
      nowY () {
        if (!this.currentT) return 0
        const hours = Number(this.currentT.format('H'))
        const min = parseInt(this.currentT.format('mm'), 10)
        return `${Math.ceil(this.calendarInterval.current * hours + (this.calendarInterval.current / 60) * min)}px`
      },
      playedProgress () {
        if (!this.currentEvent) return 0
        // eslint-disable-next-line max-len
        const eventDuration = this.currentEvent.endOriginal - this.currentEvent.startOriginal
        // eslint-disable-next-line no-mixed-operators
        return (this.currentUnixTime - this.currentEvent.startOriginal) * 100 / eventDuration
      },
    },
    mounted () {
      this.updateTime()
    },
    created () {
      const channelId = this.$route.params.id

      this.loadData(channelId)
      const savedInt = localStorage.getItem('calendarInterval')
      this.calendarInterval.current = savedInt ? Number(savedInt) : this.calendarInterval.min * 2
    },
    beforeDestroy () {
      clearInterval(this.polling)
    },
    methods: {
      loadData (channelId) {
        this.$store.commit('setPageLoader')

        const tzReq = this.$store.dispatch('timezones')
        const args = {
          ids: channelId,
          expand: 'timezone,logo,watermark',
        }
        const channelReq = this.$store.dispatch('channels', args)

        const distribReqArgs = {
          'default[eq]': 1,
          'channel.id[eq]': channelId,
        }
        const distribReq = this.$store.dispatch('distributions', distribReqArgs)

        return Promise.all([tzReq, channelReq, distribReq])
          .then((res) => {
            const timezones = res[0]
            this.timezones = timezones.map((tz) => processTimezone(tz))

            const channels = res[1]
            this.channel = processChannel(channels[0])

            const duration = moment.duration(this.channel.autoSchedulerDepth, 'minutes')
            this.channel.autoSchedulerDepthStr = `${duration.hours()} hours ${duration.minutes()} minutes`
            if (duration.days()) {
              this.channel.autoSchedulerDepthStr = `${duration.days()} days ${this.channel.autoSchedulerDepthStr}`
            }

            let streamURL = this.channel.streamURLhlsv3

            const distributions = res[2] || []
            if (distributions.length) {
              const processed = processDistribution(distributions[0])
              for (const key in processed.urls) {
                if (key === 'hls3') {
                  streamURL = processed.urls[key]
                }
              }
            }

            streamURL = streamURL.replace('$origin', 'now')
            streamURL = streamURL.replace('$offset', '-1m_15s')
            streamURL = streamURL.replace(',', '_')

            this.channel.streamURL = streamURL
            this.streamFrameSrc = `/js/radiant/index3.html?src=${streamURL}`

            // eslint-disable-next-line max-len
            this.tzOffset = moment.tz(moment.tz.guess()).utcOffset() * 60 * 1000 - moment.tz(this.channel.timezone.name).utcOffset() * 60 * 1000
            this.currentDay = moment.utc(moment().utc().unix() * 1000 + moment.tz(this.channel.timezone.name).utcOffset() * 60 * 1000).format('YYYY-MM-DD')
            this.currentT = moment().tz(this.channel.timezone.name)

            this.buildWeeks()
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      buildWeeks () {
        this.$store.commit('setPageLoader')
        let w = moment.utc(moment().utc().unix() * 1000 + moment.tz(this.channel.timezone.name).utcOffset() * 60 * 1000).startOf('isoWeek').subtract(7, 'days')

        const locked = !!this.channel.autoScheduler

        this.weeks = []
        for (let i = 0; i < 7; i += 1) {
          const t = w.clone()
          const startOfWeek = t.format('MMMM D')
          const startOfWeekM = t.format('MMMM')
          const number = t.week()
          const days = []
          let lastEnd = moment()
          for (let j = 0; j < 7; j += 1) {
            const start = moment(`${t.format('YYYY-MM-DD')}T00:00:00Z`).utc().unix() * 1000 - moment.tz(this.channel.timezone.name).utcOffset() * 60 * 1000
            const end = moment(`${t.format('YYYY-MM-DD')}T23:59:59Z`).utc().unix() * 1000 - moment.tz(this.channel.timezone.name).utcOffset() * 60 * 1000

            const newDay = {
              v: t.format('YYYY-MM-DD'),
              text: t.format('MMMM D'),
              start,
              end,
              overlays: [],
              gaps: 0,
              gapsDuration: 0,
              events: 0,
            }
            t.add(1, 'days')
            if (newDay.v === this.currentDay) {
              this.currentDayObj = newDay
              this.today = newDay
            }
            days.push(newDay)
            lastEnd = moment.utc(end + moment.tz(this.channel.timezone.name).utcOffset() * 60 * 1000)
          }
          w = w.add(7, 'days')
          const endOfWeek = lastEnd.format('MMMM') === startOfWeekM ? lastEnd.utc().format('D') : lastEnd.utc().format('MMMM D')
          const newWeek = {
            text: `${startOfWeek} - ${endOfWeek}`,
            days,
            number,
            occupancy: 0,
            locked,
          }
          // init
          if (i === 1) {
            this.week = newWeek
            this.weekObj = newWeek
            this.calendarDay = this.currentDay
            this.newProgram.date = this.currentDay
          }
          this.weeks.push(newWeek)
        }
        if (!this.channel.autoScheduler) {
        // this.getBlockedWeeks();
        }
        this.getWeekStat()
        this.getPrograms(true)
      },
      getWeekStat () {
        const data = {
          query: [],
          channel: { id: this.channel.id },
        }
        for (let i = 0; i < this.weeks.length; i += 1) {
          data.query.push({
            start: `${moment(this.weeks[i].days[0].start).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
            end: `${moment(this.weeks[i].days[this.week.days.length - 1].end).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
          })
        }

        this.$store.dispatch('scheduleLoading', data)
          .then((result) => {
            for (let i = 0; i < result.query.length; i += 1) {
              this.weeks[i].occupancy = result.query[i].occupancy
            }
          /*          let gapsDuration = 0;
          for (let i = 0; i < this.week.days.length; i += 1) {
            gapsDuration += this.week.days[i].gapsDuration;
          }
          const gapPr = (gapsDuration * 100) / (7 * 60 * 60 * 24 * 1000);
          // eslint-disable-next-line no-console
          console.log('occupancy', Math.round((100 - gapPr) * 100) / 100); */
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      changeWeek (week) {
        this.week = week
        this.getPrograms()
        this.calendarDay = this.week.days[0].v
        this.newProgram.date = this.calendarDay
        this.changeDay(this.week.days[0])
      },
      updateTime () {
        this.polling = setInterval(() => {
          this.currentT = moment().tz(this.channel.tz)
          this.currentUnixTime = moment().utc().unix() * 1000
          this.findCurrentEvent()
          this.getNewEvents()
        }, 5 * 1000)
      },
      findCurrentEvent () {
        if (!this.channel.timezone || !this.channel.timezone.name) return
        const t = moment().utc().unix() * 1000
        this.currentEvent = null
        for (let j = 0; j < this.allEvents.length; j += 1) {
          const event = this.allEvents[j]
          if (t >= event.startOriginal && t <= event.endOriginal) {
            this.currentEvent = event
            break
          }
        }
        if (!this.currentEvent) {
        // this.toolbarCurrentEvent();
        }
      },
      processEvent (p) {
        const program = processProgram2(p)
        const start = moment(program.startTime).utc().unix() * 1000
        const end = moment(program.endTime).utc().unix() * 1000

        return {
          id: program.id,
          color: '#eceff1',
          start: start - this.tzOffset,
          end: end - this.tzOffset,
          startOriginal: start,
          endOriginal: end,
          program,
          // rule: p.rule ? processRule(p.rule) : {},
          fixed: p.fixed,
          timed: true,
          t: true,
        }
      },
      getEvents ({ start, end }, chEvent) {
        const min = new Date(`${start.date}T00:00:00`).getTime()
        const max = new Date(`${end.date}T23:59:59`).getTime()

        const events = []
        for (let j = 0; j < this.allEvents.length; j += 1) {
          const event = this.allEvents[j]
          if ((event.start > min && event.start < max) ||
            (event.end > min && event.end < max)) {
            events.push(event)
          }
        }

        this.events = events

        let index = 0
        if (chEvent > 0) {
          for (let j = 0; j < this.events.length; j += 1) {
            if (this.events[j].id === chEvent) {
              index = j
              break
            }
          }
        }

        if (chEvent) {
          if (this.events.length > 0) {
            this.changeEvent({ event: this.events[index] })
          } else {
            this.newEventDialog = true
          }
        }
      },
      getPrograms (init) {
        if (!this.week.days) return

        this.$store.commit('setPageLoader')

        const params = {
          'end_time[gt]': `${moment(this.week.days[0].start).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
          'start_time[lt]': `${moment(this.week.days[this.week.days.length - 1].end).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
          'channel.id[eq]': this.channel.id,
          $expand: 'items',
        }
        this.$store.dispatch('programs2', params)
          .then((programs) => {
            this.allEvents = programs.map((p) => this.processEvent(p))
            this.calcGaps()
            const day = init ? this.currentDay : this.week.days[0].v
            if (this.channel.autoScheduler) {
              this.calcDayAutoScheduler(init ? this.currentDayObj : this.week.days[0], true)
            } else {
              this.getEvents({
                start: { date: day },
                end: { date: day },
              }, true)
            }
            this.findCurrentEvent()
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      getNewEvents () {
        if (!this.channel.autoScheduler) return

        if (this.week.text !== this.weekObj.text) {
          return
        }

        let lastId = 0
        if (this.allEvents.length) {
          lastId = this.allEvents[this.allEvents.length - 1].id
        }
        const params = {
          'channel.id[eq]': this.channel.id,
          $expand: 'items',
        }
        if (lastId > 0) {
          params['id[gte]'] = lastId
        } else {
          params['start_time[gt]'] = `${moment((moment().unix() - 10 * 60) * 1000).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`
        }
        this.$store.dispatch('programs2', params)
          .then((programs) => {
            const newEvents = programs.map((p) => this.processEvent(p))
            // eslint-disable-next-line no-nested-ternary
            newEvents.sort((a, b) => ((a.start > b.start) ? 1 : ((b.start > a.start) ? -1 : 0)))
            if (this.allEvents.length) {
              const lastEvent = this.allEvents[this.allEvents.length - 1]
              for (let i = 0; i < newEvents.length; i += 1) {
                if (newEvents[i].id === lastEvent.id) {
                  for (let j = i + 1; j < newEvents.length; j += 1) {
                    this.allEvents.push(newEvents[j])
                  }
                  this.calcDayAutoScheduler(this.currentDayObj)
                  break
                }
              }
            }
          })
          .catch((errorMsg) => {
            console.log('get new events error', errorMsg)
          })
      },
      changeDay (day) {
        this.dayLoading = true
        this.currentDayObj = day
        this.calendarDay = day.v
        if (this.channel.autoScheduler) {
          this.calcDayAutoScheduler(day, true)
        }
        this.dayLoading = false
      },
      calcDay (day) {
        // eslint-disable-next-line no-param-reassign
        day.overlays = []
        // eslint-disable-next-line no-param-reassign
        day.gapsDuration = 0
        // eslint-disable-next-line no-param-reassign
        day.gaps = 0
        // eslint-disable-next-line no-param-reassign
        day.events = 0
        // eslint-disable-next-line no-param-reassign
        day.occupancy = 0

        // сортируем
        // eslint-disable-next-line no-nested-ternary,max-len
        this.allEvents.sort((a, b) => ((a.startOriginal > b.startOriginal) ? 1 : ((b.startOriginal > a.startOriginal) ? -1 : 0)))

        const events = []

        for (let j = 0; j < this.allEvents.length; j += 1) {
          const event = this.allEvents[j]
          event.topBorder = 0
          event.jumpUp = 0
          // eslint-disable-next-line max-len
          if ((event.startOriginal > day.start && event.startOriginal < day.end) ||
            (event.endOriginal > day.start && event.endOriginal < day.end)) {
            events.push(event)
          }
          if (j > 0) {
            if (event.startOriginal === this.allEvents[j - 1].endOriginal) {
              event.topBorder = 1
            } else if (this.allEvents[j - 1].endOriginal - event.startOriginal < 6 * 60 * 60 * 1000) {
              event.jumpUp = 1
            }
          } else {
            event.topBorder = 1
          }
        }

        // eslint-disable-next-line no-param-reassign
        day.events = events.length

        if (events.length > 0) {
          let maxEnd = events[0].endOriginal
          let opens = []
          opens.push(events[0])

          if (events[0].startOriginal > day.start) {
            const gapDuration = events[0].startOriginal - day.start
            if (gapDuration > 0) {
              // eslint-disable-next-line no-param-reassign
              day.gaps += 1
              // eslint-disable-next-line no-param-reassign
              day.gapsDuration += gapDuration
            }
          }

          for (let j = 1; j < events.length; j += 1) {
            const event = events[j]
            const newOpens = []
            for (let k = 0; k < opens.length; k += 1) {
              if (opens[k].endOriginal > maxEnd) {
                maxEnd = opens[k].endOriginal
              }
              if (event.startOriginal < opens[k].endOriginal) {
                day.overlays.push([event.id, opens[k].id])
                newOpens.push(opens[k])
              }
            }
            if (newOpens.length === 0) {
              const gapDuration = event.startOriginal - maxEnd
              if (gapDuration > 0) {
                // eslint-disable-next-line no-param-reassign
                day.gaps += 1
                // eslint-disable-next-line no-param-reassign
                day.gapsDuration += gapDuration
              }
            }
            newOpens.push(event)
            opens = newOpens

            if (j === events.length - 1) {
              if (day.end > event.endOriginal) {
                const gapDuration = day.end - event.endOriginal
                if (gapDuration > 0) {
                  // eslint-disable-next-line no-param-reassign
                  day.gaps += 1
                  // eslint-disable-next-line no-param-reassign
                  day.gapsDuration += gapDuration
                }
              }
            }
          }
          if (events.length === 1 && day.end > maxEnd) {
            const gapDuration = day.end - maxEnd
            if (gapDuration > 0) {
              // eslint-disable-next-line no-param-reassign
              day.gaps += 1
              // eslint-disable-next-line no-param-reassign
              day.gapsDuration += gapDuration
            }
          }
        } else {
          // eslint-disable-next-line no-param-reassign
          day.gaps += 1
          // eslint-disable-next-line no-param-reassign
          day.gapsDuration += day.end - day.start
        }

        // overlays
        const overlayMap = {}
        for (let i = 0; i < day.overlays.length; i += 1) {
          overlayMap[day.overlays[i][0]] = 1
          overlayMap[day.overlays[i][1]] = 1
        }
        // eslint-disable-next-line no-param-reassign
        day.overlayMap = overlayMap

        const gapPr = (day.gapsDuration * 100) / (60 * 60 * 24 * 1000)
        // eslint-disable-next-line no-param-reassign
        day.occupancy = Math.round((100 - gapPr) * 100) / 100
      },
      calcDayAutoScheduler (day, chEvent = false) {
        const dayStart = day.start
        const dayEnd = day.end

        this.events = []

        // eslint-disable-next-line no-nested-ternary,max-len
        this.allEvents.sort((a, b) => ((a.startOriginal > b.startOriginal) ? 1 : ((b.startOriginal > a.startOriginal) ? -1 : 0)))

        const events = []

        for (let i = 0; i < this.allEvents.length; i += 1) {
          const event = this.allEvents[i]
          if ((event.startOriginal > dayStart && event.startOriginal < dayEnd) ||
            (event.endOriginal > dayStart && event.endOriginal < dayEnd)) {
            events.push(event)
          }
        }

        // eslint-disable-next-line no-param-reassign
        day.events = events.length

        const gapEvents = []
        if (events.length > 0) {
          for (let i = 0; i < events.length; i += 1) {
            const event = events[i]
            if (i !== events.length - 1) {
              if (i === 0 && event.startOriginal - dayStart > 60) {
                gapEvents.push({
                  program: {
                    name: '---',
                    subType: '',
                  },
                  start: dayStart - this.tzOffset,
                  end: event.start,
                })
              }
              if (events[i + 1].startOriginal - event.endOriginal > 60) {
                gapEvents.push({
                  program: {
                    name: '---',
                    subType: '',
                  },
                  start: event.end,
                  end: events[i + 1].start,
                })
              }
            } else if (dayEnd - event.endOriginal > 60) {
              gapEvents.push({
                program: {
                  name: '---',
                  subType: '',
                },
                start: event.end,
                end: dayEnd - this.tzOffset,
              })
            }
            // overlays
            // eslint-disable-next-line no-prototype-builtins
            if (day.overlayMap && day.overlayMap.hasOwnProperty(event.id)) {
              event.overlap = 1
            }
          }
        } else {
          gapEvents.push({
            program: {
              name: '---',
              subType: '',
            },
            start: dayStart - this.tzOffset,
            end: dayEnd - this.tzOffset,
          })
        }

        const allEvents = events.concat(gapEvents)
        // eslint-disable-next-line no-nested-ternary
        allEvents.sort((a, b) => ((a.start < b.start) ? 1 : ((b.start < a.start) ? -1 : 0)))
        this.events = allEvents
        if (this.events.length > 0 && chEvent) {
          let noEvents = true
          for (let i = 0; i < this.events.length; i += 1) {
            if (this.events[i].id) {
              this.changeEventFromAutoScheduler(this.events[i])
              noEvents = false
              break
            }
          }
          if (noEvents) {
            this.newEventDialog = true
          }
        }
      },
      calcGaps (selectedDays) {
        for (let i = 0; i < this.week.days.length; i += 1) {
          const day = this.week.days[i]

          if (selectedDays && selectedDays.length && !selectedDays.includes(day.v)) {
            // eslint-disable-next-line no-continue
            continue
          }
          this.calcDay(day)
        }
      // this.getWeeksStat();
      },
      changeEventFromAutoScheduler (event) {
        if (!event.id) return
        const args = {
          id: event.id,
          $expand: '*',
        }
        this.$store.dispatch('getProgram2ById', args)
          .then((program) => {
            event.program = processProgram2(program)
            for (let i = 0; i < event.program.items.length; i += 1) {
              const item = event.program.items[i]
              if (item.assetResource && item.assetResource.state === 'ready') {
                item.streamURL = item.assetResource.streamURL
              }
            }
          })
          .catch((errorMsg) => {
            console.log('error getting program details', errorMsg)
          })
          .finally(() => this.changeEvent({ event }))
      },
      changeEvent ({ event }) {
        if (event.id === 'new') return
        this.newEventDialog = false
        // this.removeNewEvent();
        this.selectedEvent.event.color = '#eceff1'
        this.selectedEvent.event = event

        this.selectedEvent.date = moment(event.start).format('YYYY-MM-DD')
        this.selectedEvent.time = moment(event.start).format('HH:mm:ss')
        if (moment(event.start).format('DD') !== moment(event.end).format('DD')) {
          this.calendarDay = this.calendarDay === moment(event.start).format('YYYY-MM-DD') ? this.calendarDay : moment(event.end).format('YYYY-MM-DD')
        } else {
          this.calendarDay = moment(event.start).format('YYYY-MM-DD')
        }
        this.selectedEvent.event.color = 'primary'
        // this.selectedEvent.nextEvents = this.getNextEvents(this.selectedEvent.event.id);

        if (!event.program.duration) {
          // eslint-disable-next-line no-param-reassign
          event.loading = true
          this.$store.dispatch('programs/getProgram', event.id)
            .then((e) => {
              // eslint-disable-next-line no-param-reassign
              event = this.processEvent(e)
              this.selectedEvent.event = event
              this.selectedEvent.event.color = 'primary'
              // eslint-disable-next-line no-param-reassign
              event.loading = false
            })
            .catch((errorMsg) => {
              // eslint-disable-next-line no-param-reassign
              event.loading = false
              console.log('error getting event', event.id, errorMsg)
            })
        }
      },
      dayColor (day) {
        if (day.occupancy === undefined) return 'grey'
        if (day.occupancy >= 90) return 'green darken-3'
        if (day.occupancy >= 50 && day.occupancy < 90) return 'orange darken-2'
        return 'red darken-2'
      },
      iconError () {
        this.channel.icon = this.defaultIcon
      },
      setActiveCol (i) {
        this.activeCol = i
      },
      goToRules () {
        this.$router.push({
          name: 'ChannelEditPage',
          params: {
            id: this.channel.id,
          },
          query: {
            tab: 1,
          },
        })
      },
      deleteEvent () {
      },
      playStreamByURL (stream, name, description) {
        const link = stream.replace(/^http:\/\//, 'https://')

        this.player.name = name
        this.player.description = description
        this.frameSrc = `/js/radiant/index2.html?src=${link}`
        this.openPlayer = true
      },
      closeStream () {
        this.openPlayer = false
        this.frameSrc = '/js/radiant/index.html?src='
      },
    },
  }
</script>

<style>
  .v-event-draggable {
    user-select: none;
    -webkit-user-select: none;
    padding-left: 6px;
  }

  .v-event-top-docked {
    border-top: 1px solid grey !important;
  }

  .v-event-draggable img
  {
    position: relative;
    display: inline-block;
    vertical-align: top;
    width: 50px;
  }

  .v-radio .v-label {
    padding-top: 8px !important;
  }

  .newprogramm .v-input--checkbox .v-input__control label {
    padding-top: 8px !important;
  }

  .day-table {
    width: 100%;
  }
  .asset-table tr td {
    cursor: pointer;
  }
  .next-events {
    width: 100%;
  }
  .next-events td {
    vertical-align: top;
    line-height: normal !important;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
    padding-top: 10px;
  }
  .next-events tr:last-child td {
    border-bottom: 0;
  }
  .next-events td span {
    display: inline;
    float: left;
  }
  .vjs-custom-skin video {
    max-width: 960px !important;
  }
  .rule-table {
    width: 100%;
  }
  .rule-table tr {
    background-color: #eeeeee;
  }
  .rule-table tr td {
    padding: 5px 10px;
  }
  .rule-table tr:hover {
    cursor: pointer;
    background-color: #e6eef8;
    color: #2e67c0;
  }
  .text-no-wrap * {
    float: left;
  }
  .selected-day {
    background-color: #f0f6fc !important;
  }
  .v-calendar-daily_head-weekday {
    display: none !important;
  }
  .weeks .v-card:not(.on-hover) {
    opacity: 0.9;
  }
  .weeks .show-btns {
    color: rgba(255, 255, 255, 1) !important;
  }
  .current-week {
    background-color: #f0f6fc !important;
  }
  .table-scheduler {
    width: 100%;
    display: block;
  }
  .table-scheduler tbody {
    width: 100%;
    min-height: 150px;
    max-height: 775px;
    display: inline-block;
    overflow-y: auto;
    overflow-x: auto;
  }
  .table-scheduler tbody tr {
    width: 100%;
  }
  .table-scheduler tbody td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    padding-top: 7px;
    padding-bottom: 7px;
  }
  .table-scheduler tbody td:last-child {
    width: 100%;
  }
  .v-current-time {
    height: 2px;
    background-color: #ea4335;
    position: absolute;
    left: -1px;
    right: 0;
    pointer-events: none;
  }
  .first::before {
    content: '';
    position: absolute;
    background-color: #ea4335;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: -5px;
    margin-left: -6.5px;
  }
</style>
